import { getErrorCopy } from "./errorCodes";

/**
 * Normalize any backend error into a unified shape every error surface can
 * consume. Pass it a fetch Response (and the parsed body if you have it),
 * a thrown Error from a network failure, or a raw string. Returns:
 *
 *   {
 *     code:        number | "offline" | "default",
 *     title:       string,
 *     message:     string,
 *     fieldErrors: { [fieldName]: string }   // present only for 422/400 validation
 *   }
 *
 * Accepts these shapes:
 *   parseApiError(response, parsedBody?)        ← from fetch()
 *   parseApiError(error)                        ← from a thrown Error
 *   parseApiError({ status, error, fieldErrors }) ← convenience for tests
 *   parseApiError("Something broke")            ← string fallback
 */
export function parseApiError(input, body = null) {
  // Network failure (offline, DNS, CORS): a thrown Error, not a Response
  if (input instanceof Error) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      const copy = getErrorCopy("offline");
      return { code: "offline", ...copy };
    }
    if (input.name === "AbortError") {
      const copy = getErrorCopy(408);
      return { code: 408, ...copy, message: input.message || copy.message };
    }
    // An answer that couldn't be read (cut short, or not JSON): the
    // browser's own words ("JSON.parse: unexpected end of data…") mean
    // nothing to the person.
    if (input.name === "SyntaxError") {
      const copy = getErrorCopy("default");
      return { code: "default", ...copy, message: "Chariot's answer didn't come through whole. Reload the page and try again." };
    }
    // The request never reached Chariot (each browser words it its own way).
    if (input.name === "TypeError" && /failed to fetch|networkerror|load failed|network request failed/i.test(input.message || "")) {
      const copy = getErrorCopy("default");
      return { code: "default", ...copy, message: "Couldn't reach Chariot. Check the signal and try again." };
    }
    const copy = getErrorCopy("default");
    return { code: "default", ...copy, message: input.message || copy.message };
  }

  // String fallback
  if (typeof input === "string") {
    const copy = getErrorCopy("default");
    return { code: "default", ...copy, message: input || copy.message };
  }

  // Response object or response-like { status, ...body }
  const status = input?.status || input?.statusCode;
  const errorBody = body || (input && typeof input === "object" ? input : null);
  const copy = getErrorCopy(status);
  return {
    code: status || "default",
    title: copy.title,
    // Prefer the server's specific error message over our generic copy when
    // the server bothered to include one — it usually has more context
    // (e.g., "Customer has no email address on file"). Fall back to default.
    message:
      (errorBody && typeof errorBody.error === "string" && errorBody.error) ||
      (errorBody && typeof errorBody.message === "string" && errorBody.message) ||
      copy.message,
    fieldErrors:
      (errorBody && typeof errorBody.fieldErrors === "object" && errorBody.fieldErrors) ||
      (errorBody && typeof errorBody.details === "object" && errorBody.details) ||
      undefined,
  };
}
